import type { Message, MessageReceipt as Receipt, PublicUser } from '@itsappy/shared';
import { AvatarStack } from '@/components/ui';
import { messageTime } from '@/lib/utils';

/** How far one person has read, attached to the newest message it reaches. */
export interface SeenEntry {
  /** The readers whose account we could resolve, for the avatar stack. */
  users: PublicUser[];
  /** How many have read it, including anyone the member list did not know. */
  count: number;
  /** The most recent of their cursors — what a DM's "Seen 14:32" shows. */
  readAt: string;
}

/**
 * Where each person's read cursor lands in the transcript.
 *
 * Receipts are one timestamp per reader, so a cursor is placed on the newest
 * message of yours it has passed rather than on all of them — which is what
 * makes a group read as a column of faces moving up the conversation instead
 * of the same faces repeated under every line.
 */
export function receiptsByMessage(
  messages: Message[] | undefined,
  receipts: Receipt[] | undefined,
  viewerId: string | undefined,
  peopleById: Map<string, PublicUser>,
): Map<string, SeenEntry> {
  const placed = new Map<string, SeenEntry>();
  if (!messages || !receipts || !viewerId) return placed;

  // Only your own messages carry a receipt, and only settled ones: a message
  // still in flight has no server timestamp to compare a cursor against.
  const own = messages.filter(
    (message) => message.author.id === viewerId && !message.id.startsWith('pending-'),
  );
  if (own.length === 0) return placed;

  for (const receipt of receipts) {
    if (receipt.userId === viewerId) continue;
    let target: Message | undefined;
    for (const message of own) {
      if (message.createdAt <= receipt.readAt) target = message;
      else break;
    }
    if (!target) continue;

    const entry = placed.get(target.id) ?? { users: [], count: 0, readAt: receipt.readAt };
    const person = peopleById.get(receipt.userId);
    if (person) entry.users.push(person);
    entry.count += 1;
    if (receipt.readAt > entry.readAt) entry.readAt = receipt.readAt;
    placed.set(target.id, entry);
  }
  return placed;
}

/**
 * Delivery state for your own message.
 *
 * A DM says it in words under the last thing you sent, because there is
 * exactly one person it could be about and the time they got there is the
 * useful part. A small group says it with faces instead: "seen by 3" makes
 * the author count heads, while three avatars answer *who* — which is the
 * question a group actually asks. Bigger rooms show nothing at all; the
 * server refuses to send receipts for them (`RECEIPTS_MAX_MEMBERS`).
 */
export function MessageReceipt({
  message,
  isLastOwn,
  seen,
  variant,
}: {
  message: Message;
  /** The most recent message you sent here — where a bare marker belongs. */
  isLastOwn: boolean;
  seen?: SeenEntry;
  /** `off` in a conversation too big for receipts, or with them turned off. */
  variant: 'dm' | 'group' | 'off';
}) {
  if (variant === 'off') return null;
  const pending = message.id.startsWith('pending-');

  if (variant === 'group') {
    if (seen && seen.count > 0) {
      return (
        <span
          className="ml-1 inline-flex shrink-0 items-center gap-1"
          aria-label={
            seen.users.length
              ? `Seen by ${seen.users.map((user) => user.name).join(', ')}`
              : `Seen by ${seen.count}`
          }
        >
          {seen.users.length > 0 ? (
            <AvatarStack users={seen.users} max={4} size="xs" />
          ) : (
            <span className="text-[10px] text-brand-300">Seen by {seen.count}</span>
          )}
        </span>
      );
    }
    if (!isLastOwn) return null;
    return (
      <span className="ml-1 inline-flex shrink-0 items-center text-[10px] text-slate-500">
        {pending ? 'Sending' : 'Sent'}
      </span>
    );
  }

  if (!isLastOwn) return null;
  const label = pending ? 'Sending' : seen ? `Seen ${messageTime(seen.readAt)}` : 'Sent';
  return (
    <span
      className={`ml-1 inline-flex shrink-0 items-center text-[10px] ${seen && !pending ? 'text-brand-300' : 'text-slate-500'}`}
      title={seen && !pending ? `Read at ${messageTime(seen.readAt)}` : undefined}
    >
      {label}
    </span>
  );
}
